import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, CircleAlert, CircleCheck, HardDrive, TriangleAlert } from 'lucide-react';
import { mediaService } from '../../services/mediaService';
import { StorageHealthReport, StorageRepairResult, StorageStatus } from '../../types/media';
import { Button, Card, Dot, EmptyState, Pill, SectionHeader, Skeleton, StatGrid } from '../ui';

type Tone = 'red' | 'amber' | 'green';

export interface StorageDiagnosis {
  tone: Tone;
  text: string;
  /** Offer to copy fallback-only files into the primary store */
  repair?: boolean;
}

const files = (n: number) => `${n.toLocaleString()} ${n === 1 ? 'file' : 'files'}`;
const filesAre = (n: number) => `${files(n)} ${n === 1 ? 'is' : 'are'}`;

const storeName = (status: StorageStatus) =>
  status.type === 's3' ? `the file store at ${status.location}` : `local storage (${status.location})`;

/** S3 locations are `endpoint/bucket`; the endpoint is what people restart */
const s3Endpoint = (location: string) => location.replace(/\/[^/]+$/, '');

/** Plain-language findings, most urgent first, each with the fix. */
export function diagnoseStorage(report: StorageHealthReport): StorageDiagnosis[] {
  const { primary, fallback, missing, unverified, inFallbackOnly, checked } = report;
  const out: StorageDiagnosis[] = [];

  if (!primary.reachable) {
    const cannotServe = unverified > 0 ? ` ${filesAre(unverified)} unavailable until it's back.` : '';
    out.push({
      tone: 'red',
      text:
        primary.type === 's3'
          ? `The file store at ${s3Endpoint(primary.location)} isn't responding. Restart it in your hosting dashboard (or set USE_S3=false with a persistent volume).${cannotServe}`
          : `Local storage (${primary.location}) can't be read${primary.error ? `: ${primary.error}` : ''}. Check the volume mounted at ${primary.location} in your hosting dashboard.${cannotServe}`,
    });
  } else if (primary.writable === false) {
    out.push({
      tone: 'red',
      text: `New uploads will fail: ${storeName(primary)} doesn't accept writes${primary.error ? ` (${primary.error})` : ''}.`,
    });
  }

  if (missing > 0) {
    out.push({
      tone: 'red',
      text:
        primary.type === 'local'
          ? `${filesAre(missing)} missing from local storage (${primary.location}). The backend container most likely has no persistent volume, so redeploys delete uploaded files. Add a persistent volume mounted at ${primary.location} in your hosting dashboard, then restore or re-upload these files.`
          : `${filesAre(missing)} missing from the file store at ${primary.location}${fallback ? ' and from local storage' : ''}. Check that MINIO_S3_ENDPOINT and MINIO_S3_DEFAULT_BUCKET point at the store that holds them, then restore or re-upload these files.`,
    });
  }

  if (fallback && !fallback.reachable) {
    out.push({
      tone: 'amber',
      text:
        fallback.type === 's3'
          ? `The fallback file store at ${s3Endpoint(fallback.location)} isn't responding${missing > 0 ? ', so missing files couldn\'t be looked up there' : ''}. Restart it, or remove MINIO_S3_ENDPOINT if it's no longer used.`
          : `Local fallback storage (${fallback.location}) isn't available${fallback.error ? `: ${fallback.error}` : ''}.`,
    });
  }

  if (primary.reachable && unverified > 0) {
    out.push({
      tone: 'amber',
      text: `${files(unverified)} couldn't be checked because storage didn't answer in time. Check again.`,
    });
  }

  if (inFallbackOnly > 0 && fallback) {
    out.push({
      tone: 'amber',
      text: `${filesAre(inFallbackOnly)} only in ${storeName(fallback)}. Move them into ${storeName(primary)} so they don't depend on it.`,
      repair: primary.reachable && primary.writable !== false,
    });
  }

  if (out.length === 0) {
    out.push({ tone: 'green', text: `All ${files(checked)} are available.` });
  }
  return out;
}

const TONE_ICON = {
  red: <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} aria-hidden="true" />,
  amber: <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" strokeWidth={1.75} aria-hidden="true" />,
  green: <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" strokeWidth={1.75} aria-hidden="true" />,
};

const StoreRow: React.FC<{ label: string; status: StorageStatus | null }> = ({ label, status }) => {
  if (!status) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 text-[13px]">
        <span className="w-16 flex-shrink-0 text-neutral-500">{label}</span>
        <span className="text-neutral-400">None</span>
      </div>
    );
  }
  const state = !status.reachable
    ? { tone: 'red' as const, text: 'Unreachable' }
    : status.writable === false
      ? { tone: 'amber' as const, text: 'Read-only' }
      : { tone: 'green' as const, text: 'Reachable' };

  return (
    <div className="px-4 py-2.5 text-[13px]" data-testid={`storage-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <span className="w-16 flex-shrink-0 text-neutral-500">{label}</span>
        <Pill tone="outline">{status.type === 's3' ? 'S3' : 'Local disk'}</Pill>
        <span className="ml-auto flex flex-shrink-0 items-center gap-1.5 text-[12px] text-neutral-500">
          <Dot tone={state.tone} />
          {state.text}
        </span>
      </div>
      <p className="mt-1 truncate font-ui-mono text-[12px] text-neutral-400 sm:pl-[72px]" title={status.location}>
        {status.location}
      </p>
      {status.error && (
        <p className={`mt-1 break-words text-[12px] sm:pl-[72px] ${status.reachable ? 'text-amber-700' : 'text-primary-700'}`}>
          {status.error}
        </p>
      )}
    </div>
  );
};

const monthLabel = (month: string) => {
  const [year, m] = month.split('-').map(Number);
  if (!year || !m) return month;
  return new Date(Date.UTC(year, m - 1, 1)).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const dateLabel = (value: string) =>
  new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const errorText = (error: any, fallback: string) => {
  if (error?.response?.status === 403) return 'Only admins can check storage';
  return error?.response?.data?.error?.message || error?.response?.data?.message || fallback;
};

export const StorageHealth: React.FC = () => {
  const [report, setReport] = useState<StorageHealthReport | null>(null);
  const [checking, setChecking] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<StorageRepairResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listOpen, setListOpen] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    setError(null);
    try {
      setReport(await mediaService.getStorageHealth());
    } catch (err) {
      setError(errorText(err, 'Storage check failed'));
    } finally {
      setChecking(false);
    }
  }, []);

  const repair = useCallback(async () => {
    setRepairing(true);
    setError(null);
    setRepairResult(null);
    try {
      setRepairResult(await mediaService.repairStorage());
      await check();
    } catch (err) {
      setError(errorText(err, 'Moving files failed'));
    } finally {
      setRepairing(false);
    }
  }, [check]);

  const diagnoses = report ? diagnoseStorage(report) : [];
  const maxMonth = report ? Math.max(1, ...report.missingByMonth.map((m) => m.count)) : 1;
  const missingShare = report && report.checked > 0 ? Math.round((report.missing / report.checked) * 100) : 0;

  return (
    <section aria-label="Storage">
      <SectionHeader
        title="Storage"
        aside={
          <>
            {report && !checking && (
              <span className="hidden tabular-nums sm:inline" title={`Took ${(report.durationMs / 1000).toFixed(1)}s`}>
                {new Date(report.checkedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <Button size="sm" variant="secondary" onClick={check} loading={checking} disabled={repairing}>
              {!checking && <HardDrive className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />}
              {checking ? 'Checking' : report ? 'Check again' : 'Check storage'}
            </Button>
          </>
        }
      />

      {error && (
        <p role="alert" className="mb-3 animate-fade-up rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
        </p>
      )}

      {!report ? (
        checking ? (
          <Card divided aria-busy="true" aria-label="Checking storage">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-56" />
              </div>
            ))}
          </Card>
        ) : (
          <Card>
            <EmptyState icon={HardDrive} title="Not checked" />
          </Card>
        )
      ) : (
        <div className={`space-y-3 ${checking ? 'opacity-60 transition-opacity' : ''}`} aria-busy={checking || undefined}>
          <Card divided>
            <ul className="divide-y divide-neutral-200/70" aria-label="Diagnosis">
              {diagnoses.map((d) => (
                <li key={d.text} className="flex flex-wrap items-start gap-x-2.5 gap-y-2 px-4 py-3 text-[13px]">
                  {TONE_ICON[d.tone]}
                  <p className="min-w-0 flex-1 basis-60 break-words leading-5">{d.text}</p>
                  {d.repair && (
                    <Button size="xs" variant="dark" onClick={repair} loading={repairing} disabled={checking} className="ml-6 sm:ml-0">
                      {repairing ? 'Moving' : `Move ${files(report.inFallbackOnly)}`}
                    </Button>
                  )}
                </li>
              ))}
              {repairResult && (
                <li role="status" className="px-4 py-2.5 text-[12px] text-neutral-500">
                  Moved {files(repairResult.copied)}
                  {repairResult.failed > 0 && <span className="text-primary-700"> · {repairResult.failed} failed</span>}
                </li>
              )}
            </ul>
            <StoreRow label="Primary" status={report.primary} />
            <StoreRow label="Fallback" status={report.fallback} />
          </Card>

          <StatGrid
            items={[
              {
                label: 'Files',
                value: report.checked.toLocaleString(),
                hint:
                  report.checked < report.total
                    ? `Newest ${report.checked.toLocaleString()} of ${report.total.toLocaleString()}`
                    : report.unverified > 0
                      ? `${report.unverified.toLocaleString()} unverified`
                      : undefined,
              },
              { label: 'Available', value: report.inPrimary.toLocaleString() },
              { label: 'Only in fallback', value: report.inFallbackOnly.toLocaleString() },
              {
                label: 'Missing',
                value: report.missing.toLocaleString(),
                badge: report.missing > 0 ? <Pill tone="red">{missingShare}%</Pill> : undefined,
              },
            ]}
          />

          {report.missing > 0 && (
            <Card divided>
              <div className="px-4 py-3">
                <p className="mb-2 text-[12px] text-neutral-500">Missing by month</p>
                <ul className="space-y-1.5" aria-label="Missing by month">
                  {report.missingByMonth.map(({ month, count }) => (
                    <li key={month} className="flex items-center gap-3 text-[12px]">
                      <span className="w-20 flex-shrink-0 text-neutral-500">{monthLabel(month)}</span>
                      <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-neutral-100" aria-hidden="true">
                        <span
                          className="block h-full rounded-full bg-primary-600/60"
                          style={{ width: `${Math.max(2, (count / maxMonth) * 100)}%` }}
                        />
                      </span>
                      <span className="w-10 flex-shrink-0 text-right tabular-nums text-ink">{count.toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setListOpen((v) => !v)}
                  aria-expanded={listOpen}
                  aria-controls="storage-missing-files"
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13px] transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-600/40 ${
                    listOpen ? '' : 'rounded-b-[15px]'
                  }`}
                >
                  <span>Missing files</span>
                  <span className="flex items-center gap-1 text-[12px] text-neutral-400">
                    <span className="tabular-nums">{report.missing.toLocaleString()}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${listOpen ? 'rotate-180' : ''}`} strokeWidth={1.75} />
                  </span>
                </button>
                {listOpen && (
                  <ul id="storage-missing-files" className="max-h-80 divide-y divide-neutral-200/70 overflow-y-auto border-t border-neutral-200/70">
                    {report.missingSample.map((item) => {
                      const to = item.stakeholderId
                        ? `/stakeholders/${item.stakeholderId}`
                        : item.organizationId
                          ? `/organizations/${item.organizationId}`
                          : null;
                      const name = <span className="min-w-0 flex-1 truncate">{item.originalName}</span>;
                      return (
                        <li key={item.id} className="flex items-center gap-3 px-4 py-2 text-[13px]">
                          {to ? (
                            <Link to={to} className="flex min-w-0 flex-1 hover:underline" title="Open the profile it belongs to">
                              {name}
                            </Link>
                          ) : (
                            name
                          )}
                          <span className="flex-shrink-0 text-[12px] tabular-nums text-neutral-400">{dateLabel(item.createdAt)}</span>
                        </li>
                      );
                    })}
                    {report.missing > report.missingSample.length && (
                      <li className="px-4 py-2 text-[12px] text-neutral-400">
                        {(report.missing - report.missingSample.length).toLocaleString()} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </Card>
          )}
        </div>
      )}
    </section>
  );
};
